import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { Eyebrow, StepsSubtitle, Title } from './headings';

const TOPICS = [
  {
    key: 'Sleep',
    color: '#69B0C6',
    from: 58,
    to: 67,
    logged: 'In bed by 11, up at 6. Woke twice.',
    tip: 'Both wakes follow a coffee after 2pm. Skip it this week and we compare.',
  },
  {
    key: 'Mood',
    color: '#B48CC8',
    from: 61,
    to: 70,
    logged: 'Stress 7 today. Long one.',
    tip: 'Your 7s all follow nights under 6 hours. Tonight is the fix, not tomorrow.',
  },
  {
    key: 'Weight',
    color: '#9BC3A8',
    from: 52,
    to: 60,
    logged: 'Breakfast: eggs and toast, 366 cal.',
    tip: 'Every week you log breakfast, the scale moves. That is five in a row now.',
  },
  {
    key: 'Energy',
    color: '#E8B86D',
    from: 55,
    to: 66,
    logged: 'Flat again by 3pm.',
    tip: 'It only happens on days under 1.5L of water. Two glasses before lunch.',
  },
  {
    key: 'Immunity',
    color: '#DE8FA0',
    from: 63,
    to: 71,
    logged: 'Feeling run down. 5.5 hours last night.',
    tip: 'Two short nights in a row is where you got ill last time. Early night, protect it.',
  },
  {
    key: 'Focus',
    color: '#6DC1B3',
    from: 57,
    to: 72,
    logged: 'Morning walk done, 25 mins.',
    tip: 'Walk plus 7 hours of sleep is your sharpest combination. Today should be a good one.',
  },
];

const N = TOPICS.length;
const CYCLE_MS = 7000;
// Stage = how many of these have passed since the topic started:
// 1 "Logged" bubble, 2 typing dots, 3 coach tip, 4 topic enters the list, 5 its ring grows.
const STAGE_MS = [250, 1100, 2400, 2700, 3400];
const LAST_STAGE = STAGE_MS.length;
const RINGS = [52, 40, 28];

const topicAt = (index: number) => TOPICS[(index + N) % N];

const HowItWorksSection = () => {
  const reduceMotion = useReducedMotion();
  const [{ lap, stage }, setStep] = useState({ lap: 0, stage: 0 });
  const paused = useRef(false);

  useEffect(() => {
    if (reduceMotion) {
      setStep({ lap: 0, stage: LAST_STAGE });

      return undefined;
    }

    let frame: number;
    let last = performance.now();
    let elapsed = 0;
    let current = { lap: 0, stage: 0 };

    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      const dt = now - last;

      last = now;

      if (paused.current || dt > 200) return;

      elapsed += dt;
      let nextLap = current.lap;

      if (elapsed >= CYCLE_MS) {
        elapsed -= CYCLE_MS;
        nextLap += 1;
      }

      const nextStage = STAGE_MS.filter((ms) => elapsed >= ms).length;

      if (nextLap !== current.lap || nextStage !== current.stage) {
        current = { lap: nextLap, stage: nextStage };
        setStep(current);
      }
    };

    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [reduceMotion]);

  const index = lap % N;
  const chat = stage >= 1 ? topicAt(index) : topicAt(index - 1);
  const entered = stage >= 4 || lap === 0;
  const head = entered ? index : index - 1;
  const list = [topicAt(head), topicAt(head - 1), topicAt(head - 2)];
  const isGrowing = head === index;
  const hasGrown = stage >= 5;

  return (
    <Section data-testid="how-it-works-section">
      <Eyebrow>How it works</Eyebrow>
      <SectionTitle>Small check-ins. Big changes.</SectionTitle>
      <StepsSubtitle>
        Your coach checks in, you log what you can, and week by week the numbers
        move.
      </StepsSubtitle>

      <Scene
        onPointerEnter={(e) => {
          paused.current = e.pointerType === 'mouse';
        }}
        onPointerLeave={() => {
          paused.current = false;
        }}
      >
        <Chat>
          <Bubble $you $on={stage >= 1}>
            Logged: {chat.logged}
          </Bubble>
          <CoachRow>
            {stage === 2 ? (
              <Typing>
                <Dot />
                <Dot />
                <Dot />
              </Typing>
            ) : (
              <Bubble $on={stage >= 3}>{chat.tip}</Bubble>
            )}
          </CoachRow>
        </Chat>
        <Shot>
          <Halo />
          <img
            src="/images/sections/howItWorks/app.png"
            alt="The Shido app: food diary, today's progress and courses"
            width={1464}
            height={1456}
          />
        </Shot>
        <Stats>
          <RingSvg viewBox="0 0 120 120" aria-hidden="true">
            {list.map((topic, slot) => {
              const growing = slot === 0 && isGrowing && !hasGrown;
              const value = growing ? topic.from : topic.to;
              const gain = growing ? 0 : topic.to - topic.from;
              const r = RINGS[slot];
              const c = 2 * Math.PI * r;

              return (
                <RingSet
                  key={r}
                  $snap={growing && !reduceMotion}
                  $flash={slot === 0 && isGrowing && hasGrown}
                >
                  <circle cx="60" cy="60" r={r} />
                  <circle
                    cx="60"
                    cy="60"
                    r={r}
                    transform="rotate(-90 60 60)"
                    stroke={topic.color}
                    strokeDasharray={`${(value / 100) * c} ${c}`}
                  />
                  <circle
                    cx="60"
                    cy="60"
                    r={r}
                    transform="rotate(-90 60 60)"
                    stroke={`color-mix(in srgb, ${topic.color} 45%, #fff)`}
                    strokeDasharray={`${(gain / 100) * c} ${c}`}
                    strokeDashoffset={-((value - gain) / 100) * c}
                  />
                </RingSet>
              );
            })}
          </RingSvg>
          <Legend>
            <AnimatePresence initial={false}>
              {list.map((topic) => (
                <LegendRow
                  key={topic.key}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 40, opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.45, ease: 'easeInOut' }}
                >
                  <LegendDot style={{ background: topic.color }} />
                  <LegendLabel>{topic.key}</LegendLabel>
                  <LegendValue>{topic.to}%</LegendValue>
                  <LegendGain style={{ color: topic.color }}>
                    ▲ {topic.to - topic.from}
                  </LegendGain>
                </LegendRow>
              ))}
            </AnimatePresence>
          </Legend>
        </Stats>
      </Scene>
    </Section>
  );
};

const PINK_DOTS = '#E2A9B4';
const RING_TRACK = '#E6EBEA';

const bounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
    opacity: 0.55;
  }

  40% {
    transform: translateY(-5px);
    opacity: 1;
  }
`;

const haloSpin = keyframes`
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
`;

const ringFlash = keyframes`
  0% { stroke: ${RING_TRACK}; }
  25% { stroke: #fff; }
  100% { stroke: ${RING_TRACK}; }
`;

const Section = styled.section`
  padding: 0 50px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding: 0 20px;
  }
`;

const SectionTitle = styled(Title)`
  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin-bottom: 16px;
  }
`;

const Scene = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 500px) minmax(0, 1fr);
  gap: 24px;
  align-items: center;
  max-width: 1340px;
  margin: 48px auto 0;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    grid-template-columns: 1fr;
    gap: 32px;
    justify-items: center;
  }
`;

const Chat = styled.div`
  justify-self: center;
  width: 100%;
  max-width: 300px;
  min-height: 170px;
  text-align: left;
`;

const Bubble = styled.p<{ $you?: boolean; $on: boolean }>`
  position: relative;
  display: ${({ $you }) => ($you ? 'table' : 'inline-block')};
  margin: 0;
  padding: 12px 16px;
  border-radius: 4px;
  background: ${({ $you, theme }) => ($you ? '#D9EDF3' : theme.colors.white)};
  box-shadow: ${({ $you }) =>
    $you ? 'none' : '0 4px 14px rgb(70 83 111 / 8%)'};
  color: ${({ theme }) => theme.colors.topicBlue};
  font-size: 15px;
  font-weight: 500;
  line-height: 1.45;
  opacity: ${({ $on }) => ($on ? 1 : 0)};
  transform: ${({ $on }) => ($on ? 'none' : 'translateY(6px)')};
  transition:
    opacity 0.35s ease,
    transform 0.35s ease;
`;

const CoachRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 16px;
`;

const Typing = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 44px;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${PINK_DOTS};
  animation: ${bounce} 0.9s ease-in-out infinite;

  &:nth-child(2) {
    animation-delay: 0.15s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }
`;

const Shot = styled.div`
  position: relative;
  width: 100%;

  img {
    position: relative;
    display: block;
    width: 100%;
    height: auto;
  }

  box-sizing: border-box;
  padding: 0 74px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    box-sizing: border-box;
    padding: 0;
  }
`;

const Halo = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 65%;
  aspect-ratio: 1;
  border: 2px dashed rgb(157 195 211 / 70%);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  animation: ${haloSpin} 90s linear infinite;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 90%;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Stats = styled.div`
  justify-self: center;
  display: flex;
  align-items: center;
  gap: 16px;
  text-align: left;
`;

const RingSvg = styled.svg`
  flex: none;
  width: 116px;
  height: 116px;
`;

const RingSet = styled.g<{ $snap: boolean; $flash: boolean }>`
  circle {
    fill: none;
    stroke-width: 7;
  }

  circle:first-child {
    stroke: ${RING_TRACK};

    ${({ $flash }) =>
      $flash &&
      css`
        animation: ${ringFlash} 1.1s ease-out;
      `}
  }

  circle:not(:first-child) {
    stroke-linecap: round;
    transition: ${({ $snap }) =>
      $snap
        ? 'none'
        : 'stroke-dasharray 0.9s cubic-bezier(0.4, 0, 0.2, 1), stroke-dashoffset 0.9s cubic-bezier(0.4, 0, 0.2, 1), stroke 0.4s'};
  }
`;

const Legend = styled.ul`
  width: 214px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const LegendRow = styled(motion.li)`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  overflow: hidden;
  border-bottom: 1px dashed rgb(70 83 111 / 20%);
  color: ${({ theme }) => theme.colors.topicBlue};
  font-size: 14.5px;
  font-weight: 500;
  white-space: nowrap;

  &:last-child {
    border-bottom: 0;
  }
`;

const LegendDot = styled.i`
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
`;

const LegendLabel = styled.span`
  min-width: 58px;
  color: inherit;
  font: inherit;
`;

const LegendValue = styled.b`
  min-width: 40px;
  margin-left: auto;
  padding-left: 12px;
  color: inherit;
  font-size: 14.5px;
  text-align: right;
`;

const LegendGain = styled.em`
  min-width: 34px;
  margin-left: 6px;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
`;

export default HowItWorksSection;
